import { Fragment, useState, useEffect, useRef } from "react";
import {
  Modal,
  Button,
  Form,
  Table,
  Spinner,
  Alert,
  Row,
  Col,
  Pagination,
} from "react-bootstrap";
import {
  getAllData,
  insertInto,
  deleteData,
  createCTE,
  locationDev,
} from "../../services/Servicos";
import { getErrorMessage } from "../../services/helpers";
import ModalErrorPretty from "./ModalErrorPretty";
import ModalSuccessPretty from "./ModalSuccessPretty";

export default function ModalGerenciamentoCTEs({
  show,
  setShow,
  title,
  cargaId,
  dadosBancarios,
  onConfirmar = () => {},
}) {
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [ctes, setCtes] = useState([]);
  const [error, setError] = useState({ show: false, message: "" });
  const [success, setSuccess] = useState({ show: false, message: "" });
  const [selectedFile, setSelectedFile] = useState(null);
  const [descricao, setDescricao] = useState("");
  const [showConfirmUploadModal, setShowConfirmUploadModal] = useState(false);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (show) {
      carregarCTEs();
    }
  }, [show, cargaId]);

  const carregarCTEs = async () => {
    setLoading(true);
    try {
      const { data } = await getAllData(
        `gerenciamento-ctes/cargas/${cargaId}`,
      );
      setCtes(data || []);
    } catch (error) {
      console.error("Erro ao carregar CTEs:", error);
      setCtes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      // Validar se é um PDF
      if (file.type !== "application/pdf") {
        setError({
          show: true,
          message: "Apenas arquivos PDF são permitidos!",
        });
        e.target.value = null;
        return;
      }

      // Validar tamanho (máximo 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError({ show: true, message: "O arquivo deve ter no máximo 10MB!" });
        e.target.value = null;
        return;
      }

      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError({ show: true, message: "Arquivo do CTE não informado" });
      return;
    }

    if (!descricao.trim()) {
      setError({ show: true, message: "Descrição do CTE não informada" });
      return;
    }

    setShowConfirmUploadModal(true);
  };

  const confirmarUpload = async () => {
    setShowConfirmUploadModal(false);
    setUploadingFile(true);
    try {
      await createCTE({
        cargas_id: cargaId,
        descricao: descricao.toUpperCase(),
        arquivo: selectedFile,
      });

      setSuccess({ show: true, message: "CTE adicionado com sucesso!" });
      await carregarCTEs();
      setSelectedFile(null);
      setDescricao("");
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
    } catch (error) {
      setError({ show: true, message: getErrorMessage(error) });
    } finally {
      setUploadingFile(false);
    }
  };

  const handleExcluir = (id, filename) => {
    setItemToDelete({ id, filename });
    setShowConfirmDeleteModal(true);
  };

  const confirmarExclusao = async () => {
    if (!itemToDelete) return;

    setShowConfirmDeleteModal(false);
    setLoading(true);
    try {
      await deleteData(`gerenciamento-ctes/${itemToDelete.id}`);
      setSuccess({ show: true, message: "CTE excluído com sucesso!" });
      await carregarCTEs();
    } catch (error) {
      setError({ show: true, message: getErrorMessage(error) });
    } finally {
      setLoading(false);
      setItemToDelete(null);
    }
  };

  const handleVisualizar = async (cteId, filename) => {
    try {
      // Abre o PDF diretamente pela URL pública
      const url = `${locationDev}/files/ctes/${filename}`;
      window.open(url, "_blank");
    } catch (error) {
      setError({ show: true, message: getErrorMessage(error) });
    }
  };

  const handleConfirmarCTEs = () => {
    if (ctes.length === 0) {
      if (
        !window.confirm(
          "Nenhum CTE foi adicionado. Deseja continuar mesmo assim?",
        )
      ) {
        return;
      }
    }

    onConfirmar({ ctes, dadosBancarios });
    handleClose();
  };

  const handleClose = () => {
    setShow({ show: false });
    setSelectedFile(null);
    setDescricao("");
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("pt-BR");
  };

  return (
    <Fragment>
      <Modal show={show} onHide={handleClose} size="xl" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <i className="bi bi-info-circle me-2"></i>
            Adicione os CTEs (Conhecimentos de Transporte Eletrônico)
            relacionados a esta carga. Os arquivos serão anexados ao relatório
            final.
          </Alert>

          <h5 className="mb-2">Adicionar Novo CTE</h5>
          <hr />
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Arquivo PDF do CTE</Form.Label>
                  <Form.Control
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    disabled={uploadingFile}
                  />
                  <Form.Text className="text-muted">
                    Tamanho máximo: 10MB. Apenas arquivos PDF.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Descrição / Número do CTE</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ex: CTE-12345 ou Descrição do documento"
                    value={descricao}
                    onChange={(e) => setDescricao(e.target.value)}
                    disabled={uploadingFile}
                  />
                </Form.Group>
              </Col>
            </Row>
            {selectedFile && (
              <Alert variant="success" className="mt-4 mb-0">
                <small>
                  <strong>{selectedFile.name}</strong>
                  <br />
                  {formatFileSize(selectedFile.size)}
                </small>
              </Alert>
            )}

            <Button
              variant="primary"
              onClick={handleUpload}
              style={{ width: "200px" }}
              disabled={!selectedFile || uploadingFile}
            >
              {uploadingFile ? (
                <Fragment>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Enviando...
                </Fragment>
              ) : (
                <Fragment>
                  <i className="bi bi-cloud-upload me-2"></i>
                  Adicionar CTE
                </Fragment>
              )}
            </Button>
          </Form>
          <hr />

          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2">Carregando CTEs...</p>
            </div>
          ) : ctes.length > 0 ? (
            (() => {
              // Filtrar dados baseado no termo de busca
              const filteredData = ctes.filter((cte) => {
                const searchLower = searchTerm.toLowerCase();
                return (
                  cte.descricao.toLowerCase().includes(searchLower) ||
                  cte.cte.toLowerCase().includes(searchLower)
                );
              });

              // Lógica de paginação
              const indexOfLastItem = currentPage * itemsPerPage;
              const indexOfFirstItem = indexOfLastItem - itemsPerPage;
              const currentItems = filteredData.slice(
                indexOfFirstItem,
                indexOfLastItem
              );
              const totalPages = Math.ceil(filteredData.length / itemsPerPage);

              return (
                <Fragment>
                  <hr className="my-4" />
                  <h5>
                    CTEs Adicionados ({ctes.length}{" "}
                    {ctes.length === 1 ? "registro" : "registros"})
                  </h5>
                  <div
                    className="responsive-table"
                    style={{ position: "relative", height: "400px" }}
                  >
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Descrição</th>
                          <th>Nome do Arquivo</th>
                          <th>Data de Upload</th>
                          <th className="text-center">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentItems.map((cte, index) => (
                          <tr key={cte.id}>
                            <td>{indexOfFirstItem + index + 1}</td>
                            <td>{cte.descricao}</td>
                            <td>
                              <i className="bi bi-file-pdf text-danger me-2"></i>
                              {cte.cte}
                            </td>
                            <td>{formatDate(cte.createdAt)}</td>
                            <td className="text-center">
                              <div className="admin">
                                <span className="editar">
                                  <i
                                    className="bi bi-eye"
                                    title="Visualizar"
                                    onClick={() =>
                                      handleVisualizar(cte.id, cte.cte)
                                    }
                                  ></i>
                                </span>
                                <span className="excluir">
                                  <i
                                    className="bi bi-trash"
                                    title="Excluir"
                                    onClick={() => handleExcluir(cte.id, cte.cte)}
                                  ></i>
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  <hr />
                  <Row>
                    <Col>
                      <div className="d-flex justify-content-end">
                        <Pagination>
                          <Pagination.First
                            onClick={() => setCurrentPage(1)}
                            disabled={currentPage === 1}
                          />
                          <Pagination.Prev
                            onClick={() =>
                              setCurrentPage((prev) => Math.max(1, prev - 1))
                            }
                            disabled={currentPage === 1}
                          />

                          {[...Array(totalPages)].map((_, index) => {
                            const pageNumber = index + 1;
                            if (
                              pageNumber === 1 ||
                              pageNumber === totalPages ||
                              (pageNumber >= currentPage - 1 &&
                                pageNumber <= currentPage + 1)
                            ) {
                              return (
                                <Pagination.Item
                                  key={pageNumber}
                                  active={pageNumber === currentPage}
                                  onClick={() => setCurrentPage(pageNumber)}
                                >
                                  {pageNumber}
                                </Pagination.Item>
                              );
                            } else if (
                              pageNumber === currentPage - 2 ||
                              pageNumber === currentPage + 2
                            ) {
                              return (
                                <Pagination.Ellipsis key={pageNumber} disabled />
                              );
                            }
                            return null;
                          })}

                          <Pagination.Next
                            onClick={() =>
                              setCurrentPage((prev) =>
                                Math.min(totalPages, prev + 1)
                              )
                            }
                            disabled={currentPage === totalPages}
                          />
                          <Pagination.Last
                            onClick={() => setCurrentPage(totalPages)}
                            disabled={currentPage === totalPages}
                          />
                        </Pagination>
                      </div>
                    </Col>
                  </Row>
                </Fragment>
              );
            })()
          ) : (
            <Alert variant="warning">
              <i className="bi bi-exclamation-triangle me-2"></i>
              Nenhum CTE adicionado ainda.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer className="d-block">
          <Row className="justify-content-end">
            <Col sm="4" className="mb-2">
              <Button variant="secondary" onClick={handleClose}>
                Cancelar
              </Button>
            </Col>
            <Col sm="4" className="mb-2">
              <Button
                variant="success"
                onClick={handleConfirmarCTEs}
                disabled={loading}
              >
                <i className="bi bi-check-circle me-2"></i>
                Confirmar e Gerar Relatório
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      {/* Modal de Confirmação de Upload */}
      <Modal
        show={showConfirmUploadModal}
        onHide={() => setShowConfirmUploadModal(false)}
        centered
        style={{ backgroundColor: "#000000a4" }}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle text-warning me-2"></i>
            Confirmar Upload
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Você está prestes a adicionar o seguinte CTE:</p>
          <ul>
            <li>
              <strong>Arquivo:</strong> {selectedFile?.name}
            </li>
            <li>
              <strong>Descrição:</strong> {descricao}
            </li>
          </ul>
          <p>
            <strong>Deseja realmente continuar?</strong>
          </p>
        </Modal.Body>
        <Modal.Footer className="d-block">
          <Row>
            <Col>
              <Button
                variant="secondary"
                onClick={() => setShowConfirmUploadModal(false)}
              >
                Cancelar
              </Button>
            </Col>
            <Col>
              <Button variant="primary" onClick={confirmarUpload}>
                Sim, Adicionar CTE
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      {/* Modal de Confirmação de Exclusão */}
      <Modal
        show={showConfirmDeleteModal}
        onHide={() => setShowConfirmDeleteModal(false)}
        centered
        style={{ backgroundColor: "#000000a4" }}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle text-danger me-2"></i>
            Confirmar Exclusão
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Você está prestes a excluir o CTE:</p>
          <p>
            <strong>{itemToDelete?.filename}</strong>
          </p>
          <p>
            <strong>
              Esta ação não pode ser desfeita. Deseja realmente continuar?
            </strong>
          </p>
        </Modal.Body>
        <Modal.Footer className="d-block">
          <Row>
            <Col>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowConfirmDeleteModal(false);
                  setItemToDelete(null);
                }}
              >
                Cancelar
              </Button>
            </Col>
            <Col>
              <Button variant="danger" onClick={confirmarExclusao}>
                Sim, Excluir
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      {error.show && <ModalErrorPretty setShow={setError} {...error} />}
      {success.show && <ModalSuccessPretty setShow={setSuccess} {...success} />}
    </Fragment>
  );
}
